import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

const STORAGE_KEY = 'tc:timezone:v1';

function detectZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function loadFromStorage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function saveToStorage(tz: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, tz);
  } catch {
    // quota exceeded or private browsing — silently skip
  }
}

export function getSupportedZones(): string[] {
  const detected = detectZone();
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const zones: string[] = (Intl as any).supportedValuesOf('timeZone');
    if (!zones.includes(detected)) zones.unshift(detected);
    return zones;
  } catch {
    // Intl.supportedValuesOf not available — curated fallback
    const curated = [
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Anchorage',
      'America/Halifax',
      'America/Sao_Paulo',
      'Europe/London',
      'Europe/Paris',
      'Europe/Berlin',
      'Europe/Helsinki',
      'Africa/Johannesburg',
      'Asia/Dubai',
      'Asia/Kolkata',
      'Asia/Bangkok',
      'Asia/Singapore',
      'Asia/Shanghai',
      'Asia/Tokyo',
      'Australia/Sydney',
      'Pacific/Auckland',
    ];
    if (!curated.includes(detected)) curated.unshift(detected);
    return curated;
  }
}

export const useTimezoneStore = defineStore('timezone', () => {
  const stored = loadFromStorage();
  const selectedZone = ref<string>(stored ?? detectZone());

  watch(selectedZone, saveToStorage, { immediate: false });

  function setZone(tz: string): void {
    selectedZone.value = tz;
  }

  return { selectedZone, setZone };
});
